import { createFileRoute, Link } from "@tanstack/react-router";
import { abs } from "@/lib/site";
import { SiteHeader, SiteFooter } from "@/components/SiteChrome";
import { kaomojiCollections } from "@/data/kaomoji";

const TITLE = "Kaomoji — Japanese text faces & emoticons to copy and paste";
const DESC =
  "A big hub of kaomoji and text faces grouped by mood — happy, sad, angry, love, cute, shrug, table flip and more. Tap any face to copy it; works in any text field.";

export const Route = createFileRoute("/kaomoji/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: "Kaomoji — Japanese Text Faces" },
      { property: "og:description", content: DESC },
      { property: "og:url", content: abs("/kaomoji") },
      { property: "og:type", content: "website" },
    ],
    links: [{ rel: "canonical", href: abs("/kaomoji") }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Kaomoji",
          description: DESC,
          url: "/kaomoji",
          numberOfItems: kaomojiCollections.length,
        }),
      },
    ],
  }),
  component: KaomojiIndex,
});

function KaomojiIndex() {
  const total = kaomojiCollections.reduce((n, c) => n + c.faces.length, 0);
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="mx-auto w-full max-w-7xl px-4 pb-6 pt-8 sm:px-6 sm:pt-16">
          <nav className="mb-5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground sm:text-sm">
            <Link to="/" className="hover:text-foreground">Home</Link>
            <span>/</span>
            <span className="text-foreground">Kaomoji</span>
          </nav>
          <h1 className="font-display text-3xl font-bold sm:text-5xl">Kaomoji & Text Faces</h1>
          <p className="mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
            {total} Japanese-style emoticons grouped by mood — built from ordinary text, so they paste anywhere
            an emoji keyboard can't reach. Pick a mood, then tap any face to copy it.
          </p>
          <p className="mt-3 text-sm text-muted-foreground">
            Looking for{" "}
            <Link to="/lenny-faces" className="text-accent hover:underline">Lenny Face ( ͡° ͜ʖ ͡°)</Link>? It has
            its own page.
          </p>
        </section>

        <section className="mx-auto w-full max-w-7xl px-4 pb-16 sm:px-6">
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {kaomojiCollections.map((c) => (
              <li key={c.slug}>
                <Link
                  to="/kaomoji/$slug"
                  params={{ slug: c.slug }}
                  className="glass flex h-full flex-col rounded-xl p-5 transition-colors hover:border-primary/40"
                >
                  <span className="mb-3 flex min-h-[3.5rem] min-w-0 items-center justify-center overflow-x-auto rounded-lg border border-border bg-surface/60 px-3 py-2">
                    <span className="whitespace-pre text-center font-sans text-lg text-foreground/90">{c.hero}</span>
                  </span>
                  <span className="font-display text-lg font-semibold">{c.name}</span>
                  <span className="mt-1 text-sm text-muted-foreground">{c.tagline}</span>
                  <span className="mt-3 text-xs text-muted-foreground">{c.faces.length} faces →</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
